// src/data/shelters.ts
// --------------------
// Repository for the `shelters` table (src/data/ layer, Part B). Starts with the
// core lookup used to assemble the session user; more shelter reads/writes (admin
// list + CRUD) migrate onto it over time. Typed via getTypedClient() against the
// generated schema. Returns null on error / no client (logged).

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type ShelterRow = Database["public"]["Tables"]["shelters"]["Row"];

// The columns the session build needs. Typed as `string` (not a literal) so
// supabase-js doesn't deep-parse the select into a column-subset type.
const SHELTER_SESSION_COLS: string =
  "id, shelter_name, state, postal_line1, postal_line2, postal_suburb_city, postal_state, postal_postcode, stock_held";

export type ShelterResult = { data: ShelterRow | null; error: { message: string } | null };

// Look up a single shelter by code, returning BOTH the row and any query error
// so callers that must distinguish an RLS/query error from a genuine "not found"
// (e.g. Login's specific shelters-RLS diagnostic) can. Does NOT log — the caller
// decides how to surface the error.
export async function getShelterByCodeResult(
  shelterCode: string | undefined
): Promise<ShelterResult> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  const { data, error } = await c
    .from("shelters")
    .select(SHELTER_SESSION_COLS)
    .eq("shelter_code", shelterCode ?? "")
    .maybeSingle();
  if (error) return { data: null, error };
  // .select() with a non-literal column string types `data` as GenericStringError;
  // at runtime it's the row, so bridge through unknown.
  return { data: (data as unknown as ShelterRow) ?? null, error: null };
}

// Convenience wrapper for the common row-or-null case (most callers). Logs a
// generic warning on error; returns null if not found / no client / on error.
export async function getShelterByCode(
  shelterCode: string | undefined
): Promise<ShelterRow | null> {
  const { data, error } = await getShelterByCodeResult(shelterCode);
  if (error) logger.warn("getShelterByCode failed (possible RLS issue):", error.message);
  return data;
}

// Look up a single shelter by its surrogate id (the portal-owned internal key),
// returning BOTH the row and any query error so callers that must distinguish an
// RLS/query error from a genuine "not found" (e.g. Login's shelters-RLS
// diagnostic) can. Mirror of getShelterByCodeResult on shelter_id — the identity
// work (Phase 2) makes shelter_id the sole lookup key. Does NOT log.
export async function getShelterByIdResult(shelterId: string | undefined): Promise<ShelterResult> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  const { data, error } = await c
    .from("shelters")
    .select(SHELTER_SESSION_COLS)
    .eq("id", shelterId ?? "")
    .maybeSingle();
  if (error) return { data: null, error };
  return { data: (data as unknown as ShelterRow) ?? null, error: null };
}

// Convenience wrapper for the common row-or-null case. Logs a generic warning on
// error; returns null if not found / no client / on error. Mirror of
// getShelterByCode on shelter_id.
export async function getShelterById(shelterId: string | undefined): Promise<ShelterRow | null> {
  const { data, error } = await getShelterByIdResult(shelterId);
  if (error) logger.warn("getShelterById failed (possible RLS issue):", error.message);
  return data;
}

// The FULL shelter row (all columns) by surrogate id. ShelterDetail uses this to
// re-seed its form from DB-fresh data when a service is opened, so it never shows a
// stale admin-list cache (e.g. right after an Unleashed import ran on another
// screen). Null on error / not found / no client.
export async function getShelterByIdFull(
  shelterId: string | undefined
): Promise<ShelterRow | null> {
  const c = getTypedClient();
  if (!c || !shelterId) return null;
  const { data, error } = await c.from("shelters").select("*").eq("id", shelterId).maybeSingle();
  if (error) {
    logger.warn("getShelterByIdFull failed:", error.message);
    return null;
  }
  return (data as unknown as ShelterRow) ?? null;
}

// The DFV Service's default Unleashed warehouse CODE (shelters.default_warehouse
// stores the code, e.g. "W3" — db-58). Used as the Order Builder's warehouse_code.
// null on error / no client / not set.
export async function getShelterDefaultWarehouse(
  shelterCode: string | undefined
): Promise<string | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("shelters")
    .select("default_warehouse")
    .eq("shelter_code", shelterCode ?? "")
    .maybeSingle();
  if (error) {
    logger.warn("getShelterDefaultWarehouse failed:", error.message);
    return null;
  }
  return (
    (data as unknown as { default_warehouse: string | null } | null)?.default_warehouse ?? null
  );
}

// The DFV Service's Unleashed sales meta for a Sales Order header: its default
// warehouse CODE (db-58) and its Sales Order Group (`shelters.sales_group`). Both
// null when not set / on error / no client. One round-trip for the SO builder.
export async function getShelterSalesMeta(
  shelterCode: string | undefined
): Promise<{ default_warehouse: string | null; sales_group: string | null }> {
  const c = getTypedClient();
  if (!c) return { default_warehouse: null, sales_group: null };
  const { data, error } = await c
    .from("shelters")
    .select("default_warehouse, sales_group")
    .eq("shelter_code", shelterCode ?? "")
    .maybeSingle();
  if (error) {
    logger.warn("getShelterSalesMeta failed:", error.message);
    return { default_warehouse: null, sales_group: null };
  }
  const row = data as unknown as {
    default_warehouse: string | null;
    sales_group: string | null;
  } | null;
  return {
    default_warehouse: row?.default_warehouse ?? null,
    sales_group: row?.sales_group ?? null,
  };
}

// The DFV Service's customer_type value (for the responder header's category chip,
// db-124). Read live from the service so it reflects the CURRENT type on any
// request, old or new. null on error / no client / not found.
export async function getShelterCustomerType(
  shelterCode: string | undefined
): Promise<string | null> {
  const c = getTypedClient();
  if (!c || !shelterCode) return null;
  const { data, error } = await c
    .from("shelters")
    .select("customer_type")
    .eq("shelter_code", shelterCode)
    .maybeSingle();
  if (error) {
    logger.warn("getShelterCustomerType failed:", error.message);
    return null;
  }
  const row = data as unknown as { customer_type: string | null } | null;
  return row?.customer_type ?? null;
}

// Colleagues at a specific shelter (display_name + user_email only) via the
// get_shelter_colleagues SECURITY DEFINER RPC, so admin-only profile columns
// (internal_notes, etc.) stay hidden from shelter users. This is the CC-selector
// source in the request forms; pass the ACTIVE shelter (user.shelterUuid) so a
// multi-service user only sees colleagues at the service the request is for — the
// RPC also guards that the caller is a member of that shelter. Returns null on
// error / no client / no shelter id.
export type ShelterColleague = { display_name: string; user_email: string };
export async function getShelterColleagues(
  shelterId: string | null | undefined
): Promise<ShelterColleague[] | null> {
  const c = getTypedClient();
  if (!c || !shelterId) return null;
  try {
    const { data, error } = await c.rpc("get_shelter_colleagues", {
      p_shelter_id: shelterId,
    });
    if (error) {
      logger.warn("get_shelter_colleagues failed:", error.message);
      return null;
    }
    return (data as ShelterColleague[] | null) ?? null;
  } catch (e) {
    logger.warn("get_shelter_colleagues error:", e);
    return null;
  }
}

// All shelters, name-ordered (admin lists). Returns null on error / no client.
export async function listAllShelters(): Promise<ShelterRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c.from("shelters").select("*").order("shelter_name");
  if (error) {
    logger.warn("listAllShelters failed:", error.message);
    return null;
  }
  return data ?? [];
}

// Active DFV Services with NO primary contact nominated (primary_contact IS NULL).
// Powers the "Check Primary Contacts" audit modal. Name-ordered; deactivated
// services excluded. Returns null on error / no client.
const MISSING_PRIMARY_COLS: string = "id, shelter_code, shelter_name, state, customer_type";
export async function listSheltersMissingPrimary(): Promise<ShelterRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("shelters")
    .select(MISSING_PRIMARY_COLS)
    .is("primary_contact", null)
    .eq("deactivated", false)
    .order("shelter_name");
  if (error) {
    logger.warn("listSheltersMissingPrimary failed:", error.message);
    return null;
  }
  return (data as unknown as ShelterRow[]) ?? [];
}

// Shelters whose code is in `codes` (the multi-shelter login chooser), name-ordered.
const SHELTER_PICKER_COLS: string = "id, shelter_code, shelter_name";
export async function listSheltersByCodes(codes: string[]): Promise<ShelterRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("shelters")
    .select(SHELTER_PICKER_COLS)
    .in("shelter_code", codes)
    .order("shelter_name");
  if (error) {
    logger.warn("listSheltersByCodes failed:", error.message);
    return null;
  }
  return (data as unknown as ShelterRow[]) ?? [];
}

// Shelters whose surrogate id is in `ids` (the multi-shelter login chooser),
// name-ordered. Mirror of listSheltersByCodes on shelter_id — rows still carry
// shelter_code (SHELTER_PICKER_COLS) for display/value use at the call site.
export async function listSheltersByIds(ids: string[]): Promise<ShelterRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("shelters")
    .select(SHELTER_PICKER_COLS)
    .in("id", ids)
    .order("shelter_name");
  if (error) {
    logger.warn("listSheltersByIds failed:", error.message);
    return null;
  }
  return (data as unknown as ShelterRow[]) ?? [];
}

// Sync info for the DFV Services Push List inspect modal: code + name + the Unleashed
// Customer GUID (so the modal can show link state + whether a push creates or updates).
// [] for an empty id set; null on error / no client.
export async function listShelterSyncInfoByIds(ids: string[]): Promise<Array<{
  id: string;
  shelter_code: string;
  shelter_name: string;
  unleashed_guid: string | null;
}> | null> {
  if (!ids || ids.length === 0) return [];
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("shelters")
    .select("id, shelter_code, shelter_name, unleashed_guid")
    .in("id", ids);
  if (error) {
    logger.warn("listShelterSyncInfoByIds failed:", error.message);
    return null;
  }
  return (
    (data as unknown as Array<{
      id: string;
      shelter_code: string;
      shelter_name: string;
      unleashed_guid: string | null;
    }>) ?? []
  );
}

// ── Admin writes (Users & Shelters console + CSV import) ──────────────────────
// Writes take loose row objects (the admin/import forms build plain objects) and
// are cast to the generated Insert/Update shapes internally — aligning the form
// shape to the schema is a later enrichment. Each logs on error and returns
// { error } (mirrors the deliveryAddresses repo convention). `if (!c)` returns a
// null error so callers don't crash when the client isn't ready.

type ShelterInsert = Database["public"]["Tables"]["shelters"]["Insert"];
type ShelterPatch = Database["public"]["Tables"]["shelters"]["Update"];
type ShelterInput = object;
type WriteResult = { error: { message: string } | null };

// Insert one shelter org record. Returns the new surrogate id so callers (e.g.
// the Customer import) can set shelter_id on a related delivery_addresses row.
export async function insertShelter(
  row: ShelterInput
): Promise<{ error: { message: string } | null; id: string | null }> {
  const c = getTypedClient();
  if (!c) return { error: null, id: null };
  const { data, error } = await c
    .from("shelters")
    .insert([row as ShelterInsert])
    .select("id")
    .single();
  if (error) logger.warn("insertShelter failed:", error.message);
  return { error, id: (data?.id as string | undefined) ?? null };
}

// Update a shelter by surrogate id (admin edit form).
export async function updateShelterById(
  id: string | undefined,
  patch: ShelterInput
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("shelters")
    .update(patch as ShelterPatch)
    .eq("id", id ?? "");
  if (error) logger.warn("updateShelterById failed:", error.message);
  return { error };
}

// Shelter ids whose primary_contact FK points at one of `membershipIds`. Used by
// the caseworker edit save-path (contact-model 3c-iii): editUser delete+reinserts
// the user's user_shelters rows, which changes their ids, and shelters.primary_contact
// is ON DELETE SET NULL (db-90) — so any service this user is the Primary contact of
// must be re-pointed to the fresh membership afterward, or its Primary silently nulls.
export async function listShelterIdsByPrimaryContact(membershipIds: string[]): Promise<string[]> {
  const c = getTypedClient();
  if (!c || membershipIds.length === 0) return [];
  const { data, error } = await c
    .from("shelters")
    .select("id")
    .in("primary_contact", membershipIds);
  if (error) {
    logger.warn("listShelterIdsByPrimaryContact failed:", error.message);
    return [];
  }
  return ((data as unknown as Array<{ id: string }>) ?? []).map(function (r) {
    return r.id;
  });
}

// Update a shelter by its natural code (CSV import update path).
export async function updateShelterByCode(
  shelterCode: string,
  patch: ShelterInput
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("shelters")
    .update(patch as ShelterPatch)
    .eq("shelter_code", shelterCode);
  if (error) logger.warn("updateShelterByCode failed:", error.message);
  return { error };
}
